import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { rectSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import SelectionBackgroundIcon from '@duyank/icons/regular/SelectionBackground';
import { RiCloseLine } from '@remixicon/react';

import { LayerId, PageContext } from '@lidojs/design-core';
import { getPosition } from '@lidojs/design-utils';
import reverse from 'lodash/reverse';
import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useEditor, useSelectedLayers } from '../../hooks';
import ReverseTransformLayer from './layer/ReverseTransformLayer';
import LayerContent from './LayerContent';
import Sidebar, { SidebarProps } from './Sidebar';
import SortableLayer from './SortableLayer';
import { useTranslation } from 'react-i18next';

type LayerSidebarProps = SidebarProps;
const LayerSidebar: ForwardRefRenderFunction<
  HTMLDivElement,
  LayerSidebarProps
> = ({ ...props }, ref) => {
  const { t } = useTranslation();
  const dataRef = useRef({ isMultipleSelect: false });
  const { selectedLayerIds } = useSelectedLayers();
  const [activeId, setActiveId] = useState<LayerId | null>(null);
  const { layers, actions, activePage } = useEditor((state) => ({
    layers:
      state.pages[state.activePage] && state.pages[state.activePage].layers,
    activePage: state.activePage,
  }));
  const layerList = useMemo(() => {
    if (!layers) {
      return;
    }
    return reverse(layers['ROOT'].data.child.map((layerId) => layers[layerId]));
  }, [layers]);
  const rootLayer = useMemo(() => {
    if (!layers) {
      return;
    }
    return layers.ROOT;
  }, [layers]);

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor)
  );

  const handleDragEnd = ({ active, over }) => {
    setActiveId(null);
    if (!over) {
      return;
    }
    if (active.id !== over.id) {
      const newIndex =
        layerList.length - layerList.findIndex((f) => f.id === over.id) - 1;
      actions.moveLayerPosition(activePage, active.id, newIndex);
    }
  };

  const handleClickOption = (e: React.MouseEvent) => {
    actions.showContextMenu(getPosition(e.nativeEvent));
  };
  useEffect(() => {
    const enableMultipleSelect = (e: KeyboardEvent) => {
      dataRef.current.isMultipleSelect = e.shiftKey;
    };
    window.addEventListener('keydown', enableMultipleSelect);
    window.addEventListener('keyup', enableMultipleSelect);
    return () => {
      window.removeEventListener('keydown', enableMultipleSelect);
      window.removeEventListener('keyup', enableMultipleSelect);
    };
  }, []);

  const activeLayer = useMemo(
    () => (layerList || []).find((layer) => layer.id === activeId),
    [layerList, activeId]
  );
  const handleSelectLayer = (layerId: LayerId) => {
    actions.selectLayers(
      activePage,
      layerId,
      dataRef.current.isMultipleSelect ? 'add' : 'replace'
    );
  };

  return (
    <Sidebar {...props}>
      <PageContext.Provider value={{ pageIndex: activePage }}>
        <div
          css={(theme) => ({
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
          })}
        >
          <div
            css={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              height: 48,
              borderBottom: `1px solid ${theme.colors.stroke}`,
              padding: '0 20px',
            })}
          >
            <p
              css={(theme) => ({
                lineHeight: '48px',
                fontWeight: 600,
                color: theme.colors.content,
                flexGrow: 1,
              })}
            >
              {t('page_editor.general.layer')}
            </p>
            <div
              css={(theme) => ({
                fontSize: 20,
                flexShrink: 0,
                width: 32,
                height: 32,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              })}
              onClick={() => {
                actions.setSidebar();
              }}
            >
              <RiCloseLine />
            </div>
          </div>

          <DndContext
            modifiers={[restrictToVerticalAxis]}
            sensors={sensors}
            onDragCancel={() => setActiveId(null)}
            onDragEnd={handleDragEnd}
            onDragStart={({ active }) => {
              if (!active) {
                return;
              }
              setActiveId(active.id as LayerId);
            }}
          >
            <div
              ref={ref}
              css={(theme) => ({ flexGrow: 1, overflowY: 'auto' })}
            >
              <div
                css={(theme) => ({
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0,1fr)',
                  gridRowGap: 8,
                  padding: 16,
                })}
              >
                <SortableContext
                  items={(layerList || []).map((layer) => layer.id)}
                  strategy={rectSortingStrategy}
                >
                  {createPortal(
                    <DragOverlay>
                      {activeLayer ? (
                        <LayerContent layer={activeLayer} />
                      ) : null}
                    </DragOverlay>,
                    document.body
                  )}
                  {(layerList || []).map((layer) => (
                    <SortableLayer
                      key={layer.id}
                      layer={layer}
                      onClick={() => handleSelectLayer(layer.id)}
                      onOpenOption={handleClickOption}
                    />
                  ))}
                </SortableContext>
                {rootLayer && (
                  <div
                    css={(theme) => ({
                      background: theme.name === 'dark' ? theme.colors.backgroundItem : '#F3F3F3',
                      borderRadius: 8,
                      padding: 8,
                      cursor: 'pointer',
                      position: 'relative',
                      borderWidth: 0.5,
                      borderStyle: 'solid',
                      borderColor: selectedLayerIds.includes(rootLayer.id)
                        ? '#3d8eff'
                        : theme.colors.stroke,
                    })}
                    onMouseDown={() => handleSelectLayer(rootLayer.id)}
                  >
                    <div
                      css={(theme) => ({
                        display: 'flex',
                        alignItems: 'center',
                      })}
                    >
                      <div
                        css={(theme) => ({
                          width: 40,
                          height: 40,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          flexShrink: 0,
                        })}
                      ></div>
                      <div
                        css={(theme) => ({
                          minWidth: 0,
                          flexGrow: 1,
                        })}
                      >
                        <ReverseTransformLayer
                          hiddenChild={true}
                          layer={rootLayer}
                        />
                      </div>

                      <div
                        css={(theme) => ({
                          flexShrink: 0,
                          fontSize: 24,
                        })}
                      >
                        <SelectionBackgroundIcon />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </DndContext>
        </div>
      </PageContext.Provider>
    </Sidebar>
  );
};

export default forwardRef<HTMLDivElement, LayerSidebarProps>(LayerSidebar);
